import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { FolderOpen } from 'lucide-react'

import { Sidebar } from './Sidebar'
import { TopBar } from './TopBar'
import { ContextFeatureBar } from './ContextFeatureBar'
import { GlobalShortcuts } from './GlobalShortcuts'
import { FloatingMenu } from './FloatingMenu'
import { ProjectStackBar } from './ProjectStackBar'
import { RightPanel } from './RightPanel'
import { UpdateBanner } from './UpdateBanner'
import { AppFooter } from './AppFooter'
import { useIsLaptop } from '@/hooks/useMediaQuery'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { GrcCopilotSidebar } from '@/components/shared/copilot/GrcCopilotSidebar'
import { CopilotActionProvider } from '@/lib/copilot/CopilotActionProvider'
import { HITLGate } from '@/lib/copilot/HITLGate'
import { ProcessPreview } from '@/lib/copilot/ProcessPreview'
import { useJourneyStore } from '@/stores/useJourneyStore'
import { useDossierStore } from '@/stores/useDossierStore'
import { useCopilotEntityStore } from '@/stores/useCopilotEntityStore'
import { useConsentStore } from '@/stores/useConsentStore'
import { getRightPanelCardsForContext } from '@/config/right-panel'
import { cn } from '@/lib/utils'

/** Expose navigate function for E2E tests */
function NavigationExposer() {
  const navigate = useNavigate()
  useEffect(() => {
    ;(window as any).__navigate = (path: string) => navigate(path)
    return () => { delete (window as any).__navigate }
  }, [navigate])
  return null
}

const STATUS_LABEL: Record<string, string> = {
  actif: 'Actif',
  en_attente: 'En attente',
  complete: 'Complété',
  archive: 'Archivé',
}

export function MainLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const location = useLocation()

  // #RESP-R3 laptop gap (1024–1279px): the persistent sidebar was gated to `xl`
  // (1280+), so laptops had NO sidebar — only the hamburger. It now renders from
  // `lg` (1024+) but is FORCE-collapsed (68px rail) in the laptop band so it
  // never crushes <main>; at `xl`+ the user's own collapse toggle applies.
  const isLaptop = useIsLaptop()
  const effectiveSidebarCollapsed = isLaptop ? true : sidebarCollapsed

  const activeContext = useJourneyStore((s) => s.activeContext)
  const activeDossierId = useJourneyStore((s) => s.activeDossierId)
  const setActiveDossier = useJourneyStore((s) => s.setActiveDossier)
  const dossiers = useDossierStore((s) => s.dossiers)
  const activeDossier = useMemo(
    () => dossiers.find((d) => d.id === activeDossierId) || null,
    [dossiers, activeDossierId],
  )

  const { moduleContext, pageContext } = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    return {
      moduleContext: segments[0] || 'dashboard',
      pageContext: segments[segments.length - 1] || 'overview',
    }
  }, [location.pathname])

  // #CB1 — thread the currently-viewed detail entity into the Copilot so the
  // chat/advise calls are grounded on it (e.g. "résume ce risque"). Detail pages
  // publish it via useRegisterCopilotEntity; here we normalize it into the
  // entityData shape GrcCopilotSidebar expects (entity_type / entity_id / id).
  const copilotEntity = useCopilotEntityStore((s) => s.entity)
  const copilotEntityData = useMemo(() => {
    if (!copilotEntity) return undefined
    return {
      entity_type: copilotEntity.entityType,
      entity_id: copilotEntity.entityId,
      id: copilotEntity.entityId,
      ...(copilotEntity.data ?? {}),
    }
  }, [copilotEntity])

  // #CB3 — when the first-visit consent banner is visible it is fixed to the
  // viewport bottom and can overlay the last element on a detail page (the RAG
  // "Analyser" button). Reserve space at the bottom of the scroll area so the
  // button is always clickable for pre-consent users.
  const consentBannerVisible = useConsentStore((s) => !s.hasResponded && s.showBanner)

  // #RIGHTRAIL-honesty: the persona cards are now honest navigational quick-links
  // (no fabricated data). Prepend a LIVE card built from the user's REAL dossiers
  // (already in useDossierStore) so the rail shows genuine per-tenant content
  // when it exists, instead of the old hardcoded "Acme"/agenda fake data.
  const rightCards = useMemo(() => {
    const base = getRightPanelCardsForContext(activeContext)
    const activeDossiers = dossiers.filter((d) => d.status !== 'archive')
    if (activeDossiers.length === 0) return base
    const liveCard = {
      kind: 'list' as const,
      id: 'my-dossiers',
      title: 'Mes dossiers',
      icon: FolderOpen,
      accent: 'blue' as const,
      items: activeDossiers.slice(0, 5).map((d) => ({
        id: d.id,
        primary: d.label,
        secondary: STATUS_LABEL[d.status] ?? d.type,
        meta: typeof d.progress === 'number' ? `${d.progress}%` : undefined,
      })),
    }
    return [liveCard, ...base]
  }, [activeContext, dossiers])

  return (
    <CopilotActionProvider>
    <div className="flex h-screen overflow-hidden bg-background">
      {/* WCAG 2.4.1 Bypass Blocks — skip-to-content link, first focusable element. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Passer au contenu principal
      </a>
      <NavigationExposer />
      {/* App-wide keyboard shortcuts (Ctrl/⌘K, /, n, g-sequences) + ? help overlay */}
      <GlobalShortcuts />
      <Sidebar
        collapsed={effectiveSidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        forcedCollapsed={isLaptop}
      />

      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        <UpdateBanner />
        <TopBar />

        {/* Context feature bar — key features of the active persona (shortcut layer,
            just below the context selector). Accent-aware per persona; aligned with
            the mobile FeatureBar / personaTabs feature sets. */}
        <ContextFeatureBar />

        {activeDossier && (
          <ProjectStackBar
            title={activeDossier.label}
            type={STATUS_LABEL[activeDossier.status] ?? activeDossier.type}
            icon={FolderOpen}
            accent="blue"
            progress={activeDossier.progress}
            onClose={() => setActiveDossier(null)}
          />
        )}

        <div className="flex flex-1 min-h-0 overflow-hidden">
          <main
            id="main-content"
            tabIndex={-1}
            className={cn(
              'flex-1 min-w-0 min-h-0 overflow-hidden',
              'transition-all duration-300 ease-in-out',
              'focus:outline-none',
            )}
          >
            {/* Reduce padding on mobile (p-3 = 12px) to give content more
                breathing room on a 390px viewport; restore p-6 from sm:. */}
            <div
              className={cn(
                'h-full overflow-auto p-3 sm:p-6 scrollbar-thin',
                // Keep the last element (RAG "Analyser" button) above the
                // fixed consent banner until the user has responded.
                consentBannerVisible && 'pb-48',
              )}
            >
              <ErrorBoundary key={location.pathname}>
                <Outlet />
              </ErrorBoundary>
            </div>
          </main>

          <RightPanel
            cards={rightCards}
            collapsed={rightCollapsed}
            onToggle={() => setRightCollapsed((c) => !c)}
          />
        </div>

        {/* App-level footer band (spans content + right rail). First footer in
            the client shell — R5. Compact single row, hidden on phones. */}
        <AppFooter />
      </div>

      <FloatingMenu />

      <GrcCopilotSidebar
        moduleContext={moduleContext}
        pageContext={pageContext}
        entityData={copilotEntityData}
      />

      {/* HITL Confirmation Gate — Phase 2b */}
      <HITLGate />

      {/* Process/chain preview — Phase 3a */}
      <ProcessPreview />
    </div>
    </CopilotActionProvider>
  )
}
